<template>
    <div class="vx-file-upload" ref="loader">
        <label v-if="label" class="vs-select--label">{{ label }}</label>
        <vx-scroll-area>
            <div
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="receiveFileDrop($event)"
            >
                <div :class="{ hidden: !hovering }" class="overlay-wrapper">
                    <slot name="dropzone" :canDrop="canReceive">
                        <ul class="drag-overlay p-2" :class="{ error: !canReceive }">
                            <li v-for="error in errorMessages" :key="error">
                                <strong> - {{ error }} </strong>
                            </li>
                        </ul>
                    </slot>
                </div>

                <vs-upload
                    ref="uploader"
                    class="vs-upload"
                    :show-upload-button="true"
                    :automatic="false"
                    :text="text"
                    :multiple="limit == null || limit > 1"
                    :limit="limit"
                />
            </div>
        </vx-scroll-area>
    </div>
</template>

<script>
export default {
    name: 'vx-file-upload',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        text: {
            type: String,
            default: 'Upload a File'
        },
        limit: {
            type: [Number, String],
            default: Infinity
        },
        label: {
            type: String,
            default: null
        },
        allowTypes: {
            type: [String, Array],
            default: () => []
        }
    },
    watch: {
        localfiles(newV, oldV) {
            var newFiles = newV.map((fileInfo) => fileInfo.file)
            var removals = this.currentFiles.where((file) => {
                return !newFiles.contains(file)
            })

            removals.forEach((removedFile) => {
                var index = this.files.indexOf(removedFile)
                if (index < 0) {
                    return
                }
                this.$refs.uploader.removeFile(index)
            })
        }
    },
    data() {
        return {
            files: [],
            srcs: [],
            removals: [],
            hovering: false,
            loadCheck: null,
            initialized: false,
            incomingDrops_x: []
        }
    },
    computed: {
        localfiles: {
            get() {
                return this.value
            },
            set(val) {
                var resultingFiles = val.map((fi) => fi.file)
                var currentFiles = this.value.map((fi) => fi.file)
                if (!resultingFiles.equivalent(currentFiles)) {
                    this.$emit('input', val)
                }
            }
        },
        incomingDrops: {
            get() {
                return [...this.incomingDrops_x]
            },
            set(v) {
                this.incomingDrops_x = v
            }
        },
        loading_file() {
            return this.srcs.length !== this.files.length
        },
        canReceiveLimit() {
            return this.localfiles.length + this.incomingFileCount <= this.limit
        },
        canReceiveTypes() {
            return this.validIncomingFileCount == this.incomingFileCount
        },
        canReceive() {
            return this.canReceiveLimit && this.canReceiveTypes
        },
        allowedTypes() {
            if (!this.allowTypes) {
                return []
            } else if (typeof this.allowTypes === 'string') {
                return [this.allowTypes]
            }
            return this.allowTypes
        },
        incomingFileCount() {
            return this.incomingDrops.count((f) => f.kind === 'file')
        },
        validIncomingDrops() {
            return this.incomingDrops.where((f) => this.checkType(f) && f.kind === 'file')
        },
        validIncomingFileCount() {
            return this.validIncomingDrops.length
        },
        invalidTypeMessage() {
            return `Can only upload ${this.allowedTypes.textualize('and')} files`
        },
        invalidLimitMessage() {
            return `Limited to ${this.limit} files`
        },
        errorMessages() {
            var msgs = []
            if (!this.canReceiveLimit) {
                msgs.push(`Limited to ${this.limit} files`)
            }
            if (!this.canReceiveTypes) {
                msgs.push(`Can only upload ${this.allowedTypes.textualize('or')} files`)
            }
            return msgs
        },
        currentFiles() {
            return this.files.where((file, index) => !this.removals.contains(index))
        }
    },
    methods: {
        onDragOver(event) {
            this.hovering = true
            this.incomingDrops = event.dataTransfer.items
        },
        onDragLeave(event) {
            this.hovering = false
            this.incomingDrops = []
        },
        parseFiles() {
            this.loading = true
            if (this.files.length != this.srcs.length) {
                return
            }
            var index = this.files.length
            var result = []

            for (var i = 0; i < index; i++) {
                if (this.removals.includes(i)) {
                    continue
                }
                var file = this.files[i]

                result.push({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    base64: this.srcs[i].src,
                    file: file
                })
            }
            this.localfiles = result
            this.loading = false
        },
        async receiveFileDrop(event) {
            this.hovering = false
            try {
                if (!event.dataTransfer.files[0]) {
                    return
                }
                if (!this.canReceive) {
                    this.shake()
                    return
                }

                this.$refs.uploader.getFiles({
                    target: {
                        value: event.dataTransfer,
                        files: event.dataTransfer.files
                    }
                })
            } finally {
                this.incomingDrops = []
            }
        },
        checkType(file) {
            if (!this.allowedTypes.any()) {
                return true
            }
            return this.allowedTypes.any((t) => {
                return file.type && (file.type.startsWith(`${t}/`) || file.type.endsWith(`/${t}`))
            })
        }
    },
    mounted() {
        if (this.initialized) {
            return
        }
        // MASSIVE HACK! Still awesome though!
        this.$watch('$refs.uploader.itemRemove', (new_value, old_value) => {
            this.removals = new_value
            this.parseFiles()
        })
        this.$watch('$refs.uploader.srcs', (new_value, old_value) => {
            this.srcs = new_value
            this.parseFiles()
        })
        this.$watch('$refs.uploader.filesx', (new_value, old_value) => {
            this.files = new_value
            this.parseFiles()
        })

        if (this.value.any()) {
            var evt = {
                dataTransfer: {
                    value: { fakeEvent: true },
                    files: { ...this.value.map((f) => f.file) }
                }
            }

            this.receiveFileDrop(evt)
        }
        this.initialized = true
    }
}
</script>

<style scoped>
.vs-upload >>> .con-img-upload {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    border: 2px dashed rgba(0, 0, 0, 0.1);
}
.hidden {
    display: none;
}
.overlay-wrapper {
    pointer-events: none;
    position: absolute;
    height: 98%;
    width: 100%;
    z-index: 10000;
}
.drag-overlay.error {
    border: 2px dashed rgba(var(--vs-danger), 0.5);
    background-color: rgba(var(--vs-danger), 0.1);
    color: rgba(var(--vs-danger), 1);
}
.drag-overlay {
    border: 2px dashed rgba(var(--vs-primary), 0.5);
    background-color: rgba(var(--vs-primary), 0.1);
    opacity: 1;
    height: 100%;
    width: 100%;
    border-radius: 10px;
}
.file-hover {
    background-color: gray;
}
.vs-upload >>> .con-img-upload > .con-input-upload:hover {
    transform: scale(0.98);
}
.vs-upload >>> .btn-upload-all {
    pointer-events: none;
}
.vs-upload >>> .img-upload > .btn-upload-file {
    pointer-events: none;
}
.vs-upload >>> .btn-upload-file > i {
    display: none;
}
.vs-upload >>> .removeItem {
    display: none;
}
</style>
